import type { Receipt, ReceiptStatus, ReceiptSubStatus } from './receiptsTypes';

/** Receipts created on-device carry a `local-` id until the receipt service assigns a real one. */
export const isLocalReceipt = (receipt: Pick<Receipt, 'id'>): boolean => receipt.id.startsWith('local-');

/** Statuses that resolve on their own; poll while any receipt is in one of these. */
export const IN_FLIGHT_RECEIPT_STATUSES: readonly ReceiptStatus[] = ['UPLOAD_PENDING', 'UPLOADED', 'SCANNING'];

export const isInFlightReceiptStatus = (status: ReceiptStatus): boolean => IN_FLIGHT_RECEIPT_STATUSES.includes(status);

/**
 * UNPROCESSABLE is a permanent extraction failure: the retry endpoint rejects it with 409 and the
 * correct endpoint won't accept it, so the only recourse is deleting the receipt. UNKNOWN (a status
 * this client doesn't recognize yet) gets the same conservative treatment. FAILED stays retryable.
 */
export const isTerminalReceiptErrorStatus = (status: ReceiptStatus): boolean =>
  status === 'UNPROCESSABLE' || status === 'UNKNOWN';

export const isProcessingReceipt = (receipt: Pick<Receipt, 'status'>): boolean =>
  isInFlightReceiptStatus(receipt.status);

export const isProcessingErrorReceipt = (
  receipt: Pick<Receipt, 'status' | 'targetTransactionId' | 'linkFailed'>,
): boolean =>
  receipt.status === 'FAILED' ||
  isTerminalReceiptErrorStatus(receipt.status) ||
  (receipt.status === 'READY' && Boolean(receipt.targetTransactionId && receipt.linkFailed));

/** Receipts shown in the processing/error section instead of the regular reviewable list. */
export const isProcessingOrErrorReceipt = (
  receipt: Pick<Receipt, 'status' | 'targetTransactionId' | 'linkFailed'>,
): boolean =>
  isProcessingReceipt(receipt) ||
  isProcessingErrorReceipt(receipt) ||
  (receipt.status === 'READY' && Boolean(receipt.targetTransactionId));

export type ReceiptStatusKind =
  | 'uploading'
  | 'processing'
  | 'failed'
  | 'unprocessable'
  | 'staged'
  | 'linking'
  | 'linkFailed'
  | 'ready'
  | 'reviewed';

export type ReceiptStatusFields = Pick<
  Receipt,
  'status' | 'transactionId' | 'targetTransactionId' | 'linkFailed' | 'isLinking' | 'linkQueued'
>;

export const getReceiptStatusKind = (receipt: ReceiptStatusFields): ReceiptStatusKind => {
  // Receipt Center lists any linked receipt as reviewed. Only transaction-originated work still in
  // flight reports its processing or link state.
  if (receipt.transactionId && !receipt.targetTransactionId) {
    return 'reviewed';
  }

  switch (receipt.status) {
    case 'UPLOAD_PENDING':
      return 'uploading';
    case 'FAILED':
      return 'failed';
    case 'UPLOADED':
    case 'SCANNING':
      return 'processing';
    case 'UNPROCESSABLE':
    case 'UNKNOWN':
      return 'unprocessable';
    case 'READY':
      if (receipt.targetTransactionId) {
        if (receipt.linkFailed) return 'linkFailed';
        return receipt.isLinking || receipt.linkQueued ? 'linking' : 'staged';
      }
      return 'ready';
  }
};

export const isAttachingToTransaction = (receipt: ReceiptStatusFields): boolean => {
  const statusKind = getReceiptStatusKind(receipt);
  return statusKind === 'staged' || statusKind === 'linking';
};

/** Targeted work that closing or refreshing its transaction editor would return to Receipt Center. */
export const isUnsavedTargetedReceipt = <T extends ReceiptStatusFields>(receipt: T | undefined): receipt is T =>
  Boolean(receipt?.targetTransactionId && !receipt.isLinking && !receipt.linkQueued && !receipt.linkFailed);

const RECEIPT_ERROR_MESSAGES: Partial<Record<ReceiptSubStatus, string>> = {
  UPLOAD_FAILED: 'We couldn’t upload this file.',
  UPLOAD_EXPIRED: 'This upload took too long to finish.',
  MALWARE_DETECTED: 'This file may be unsafe to upload.',
  QUOTA_EXCEEDED: 'You’ve reached the receipt upload limit.',
  STORAGE_NOT_READY: 'Uploads are temporarily unavailable.',
  CREATION_FAILED: 'We couldn’t finish adding this receipt.',
  EXTRACTION_FAILED: 'We couldn’t extract the receipt details.',
  NOT_A_RECEIPT: 'We couldn’t find a receipt in this file.',
  UNSUPPORTED_FORMAT: 'This file format isn’t supported.',
  UNREADABLE: 'We couldn’t read this receipt clearly.',
};

export const getUnprocessableReceiptMessage = (receipt: Pick<Receipt, 'subStatus'>): string => {
  return (receipt.subStatus ? RECEIPT_ERROR_MESSAGES[receipt.subStatus] : undefined) ?? "Couldn't be processed";
};

/** User-facing status copy shared by web and mobile. Empty for ready, reviewed, and staged receipts. */
export const getReceiptStatusLabel = (receipt: ReceiptStatusFields & Pick<Receipt, 'subStatus'>): string => {
  const statusKind = getReceiptStatusKind(receipt);
  if (statusKind === 'linking') return 'Attaching receipt';
  if (statusKind === 'linkFailed') return "Couldn't attach this receipt. Try again.";

  const errorMessage = receipt.subStatus ? RECEIPT_ERROR_MESSAGES[receipt.subStatus] : undefined;
  if (errorMessage) {
    return errorMessage;
  }

  switch (statusKind) {
    case 'uploading':
      return 'Uploading';
    case 'processing':
      return 'Extracting details';
    case 'failed':
      return 'Extraction failed';
    case 'unprocessable':
      return getUnprocessableReceiptMessage(receipt);
    default:
      return '';
  }
};

/** Includes the completed state that remains visible in a processing panel for the current session. */
export const getProcessingPanelReceiptStatusLabel = (
  receipt: ReceiptStatusFields & Pick<Receipt, 'subStatus'>,
): string => {
  if (receipt.transactionId && !receipt.targetTransactionId) {
    return 'Reviewed';
  }
  if (receipt.status === 'READY' && receipt.targetTransactionId) {
    return getReceiptStatusLabel(receipt);
  }

  return receipt.status === 'READY' ? 'Ready to review' : getReceiptStatusLabel(receipt);
};

export type ReceiptRetryAction = 'refresh' | 'reupload' | 'recreate' | 'retry' | 'relink';

/**
 * Decides how to recover a receipt the user asked to retry:
 * - `refresh`: terminal statuses can't be retried (the server would 409); re-sync instead.
 * - `reupload`: the device file never reached storage; upload it again from `localFileUri`.
 * - `recreate`: the file uploaded but the receipt resource was never created; create it again.
 * - `retry`: a service-owned receipt; call the retry endpoint.
 * - `relink`: extraction finished, but attaching to the target transaction failed.
 */
export const getReceiptRetryAction = (receipt: Receipt): ReceiptRetryAction => {
  if (getReceiptStatusKind(receipt) === 'linkFailed') {
    return 'relink';
  }
  if (isTerminalReceiptErrorStatus(receipt.status)) {
    return 'refresh';
  }

  if (receipt.localFileUri) {
    return 'reupload';
  }

  if (isLocalReceipt(receipt)) {
    return 'recreate';
  }

  return 'retry';
};

/**
 * Local placeholders can be dropped immediately unless a create request may already have reached
 * the receipt service. Uncertain creates must reconcile to a real service id before deletion.
 */
export const shouldRemoveReceiptLocallyOnDelete = (receipt: Receipt): boolean =>
  isLocalReceipt(receipt) && receipt.needsReconciliation !== true;
